/**
 * WebAuthn integration for passkey authentication
 * Handles registration and authentication using platform authenticators
 * (FaceID, TouchID, Windows Hello, etc.)
 *
 * Note: The underlying Dexie table and credential type retain the "biometric" name
 * because renaming them would require a database migration. The public API uses
 * "passkey" to match user-facing terminology.
 */

import * as Sentry from '@sentry/react';
import { BiometricCredential } from '@/db/types/biometric_credential';
import { bufferToHex, hexToBuffer, bufferToBase64, deriveEncryptionKey } from './encryption';
import { ensureDatabaseOpen } from '@/db/db';
import {
  getPasskeyCredential,
  savePasskeyCredential,
  clearPasskeyCredential as clearPasskeyCredentialService,
  isPasskeyRegistered as isPasskeyRegisteredService,
} from '@/db/services/credential_service';
import { getCurrentUser } from '@/db/services/user_service';
import { createLogger, LogLevel } from './logger';

const logger = createLogger('WebAuthn', LogLevel.INFO);

/**
 * Custom error class for passkey authentication failures
 */
export class PasskeyError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = 'PasskeyError';
  }
}

/**
 * Check if WebAuthn is supported in the current browser
 */
export function isWebAuthnSupported(): boolean {
  return typeof window !== 'undefined' && 'PublicKeyCredential' in window;
}

/**
 * Wait briefly for the page to gain focus before invoking a WebAuthn
 * ceremony. Browsers refuse `navigator.credentials.create()` / `.get()`
 * with `NotAllowedError: ... does not have focus` when the page is in
 * the background, so we either defer the call until the user clicks
 * back in or fail fast with a clear PasskeyError the UI can act on.
 *
 * The 1s default keeps us safely inside the WebAuthn transient-activation
 * window (5s in Chromium-based browsers), so a short Cmd-Tab and back
 * still completes successfully.
 */
async function waitForFocus(timeoutMs = 1000): Promise<void> {
  if (document.hasFocus()) return;

  return new Promise<void>((resolve, reject) => {
    const onFocus = () => {
      // Browsers fire 'focus' before document.hasFocus() flips to true in
      // some cases — re-check, and stay subscribed until both agree.
      if (document.hasFocus()) {
        cleanup();
        resolve();
      }
    };

    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new PasskeyError(
        'NOT_FOCUSED',
        'The browser tab needs to be focused to use a passkey. Please click on this tab and try again.'
      ));
    }, timeoutMs);

    function cleanup() {
      clearTimeout(timeoutId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    }

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
  });
}

/**
 * Returns true when the platform offers a built-in user-verifying
 * authenticator (Touch ID, Face ID, Windows Hello, ChromeOS PIN, etc).
 * Defaults to false when the detection API is missing — that matches the
 * "no platform authenticator" branch of registration, which falls back to
 * a roaming security key. Cross-device QR pairing is intentionally not
 * offered (offline-first), so it is not part of the fallback set.
 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  const fn = (PublicKeyCredential as unknown as {
    isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean>;
  }).isUserVerifyingPlatformAuthenticatorAvailable;
  if (typeof fn !== 'function') return false;
  try {
    return await fn();
  } catch {
    return false;
  }
}

/**
 * The kind of authenticator the user explicitly chose to register with.
 * When omitted, registration auto-detects: platform if a user-verifying
 * platform authenticator is reported, otherwise a roaming security key.
 * Both branches set explicit attachment + hints so the OS picker is
 * steered away from cross-device QR pairing even when no method is
 * supplied — keeping the offline-first invariant uniform across callers.
 *
 * Only offline-capable methods are offered: the platform authenticator
 * (Windows Hello / Touch ID / Face ID and password-manager passkeys that
 * integrate via the platform path) and roaming security keys. Cross-device
 * QR pairing (caBLE / hybrid) is intentionally not offered because it
 * requires an internet relay, which contradicts the app's offline-first
 * requirement.
 */
export type PasskeyMethod = 'this-device' | 'security-key';

/**
 * Register a new passkey credential for the user
 * This should be called once after user logs in and selects their person/organization
 *
 * Credentials are scoped to the authenticated user (user.id) so they work
 * across every org/person that user belongs to. Legacy credentials registered
 * before this change were scoped to person.id and are still honored during
 * authentication (we derive the encryption key from whatever userId is stored
 * on the credential).
 *
 * @param userId - The user.id of the authenticated user
 * @param method - Optional explicit choice of authenticator (this device,
 *   security key). When omitted, the request is auto-shaped from the
 *   platform-authenticator probe.
 * @returns Promise<BiometricCredential> - The stored credential information
 * @throws PasskeyError if registration fails
 */
export async function registerPasskey(
  userId: number,
  method?: PasskeyMethod,
): Promise<BiometricCredential> {
  // 1. Check WebAuthn support
  if (!isWebAuthnSupported()) {
    throw new PasskeyError(
      'NOT_SUPPORTED',
      'WebAuthn is not supported in this browser. Please use a modern browser with passkey support.'
    );
  }

  // 2. Get current user to access email
  const currentUser = await getCurrentUser();
  const userEmail = currentUser?.email || `user_${userId}`;

  // 3. Generate challenge (cryptographically random 32 bytes)
  const challenge = crypto.getRandomValues(new Uint8Array(32));

  // 4. Probe platform availability for telemetry. When the user picked an
  // explicit method, we honor that choice instead of the probe — they may
  // intentionally want a security key on a Hello-capable laptop.
  const platformAvailable = await isPlatformAuthenticatorAvailable();

  // Translate the explicit method (if any) into the WebAuthn shape. When
  // method is omitted, auto-detect: platform shape if the OS reports a
  // usable platform authenticator, otherwise the security-key shape. Both
  // branches set explicit attachment + hints so the auto-detect path
  // can't accidentally let the OS picker offer cross-device QR pairing
  // (the only way it could surface is with both fields unset).
  // `hints` is WebAuthn L3 and is silently ignored by older browsers, so
  // we can include it without a feature check. The `hybrid` hint is never
  // requested — that flow requires an internet relay and is incompatible
  // with the app's offline-first requirement.
  let attachment: 'platform' | 'cross-platform';
  let hints: ('client-device' | 'security-key')[];
  if (method === 'this-device' || (method === undefined && platformAvailable)) {
    attachment = 'platform';
    hints = ['client-device'];
  } else {
    attachment = 'cross-platform';
    hints = ['security-key'];
  }

  Sentry.logger.info('passkey.register.attempt', {
    passkey_step: 'register',
    platform_authenticator_available: platformAvailable,
    method: method ?? 'auto',
  });

  const authenticatorSelection: AuthenticatorSelectionCriteria = {
    userVerification: 'required',
    residentKey: 'preferred',
    authenticatorAttachment: attachment,
  };

  // 5. Create credential options
  const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions & {
    hints?: ('client-device' | 'security-key')[];
  } = {
    challenge,
    rp: {
      name: 'Mon Ami GO',
      id: window.location.hostname, // e.g., 'go.monami.test' or 'app.development.monami.io'
    },
    user: {
      id: new TextEncoder().encode(userId.toString()),
      name: userEmail,
      displayName: 'Mon Ami User',
    },
    pubKeyCredParams: [
      { alg: -7, type: 'public-key' }, // ES256 (ECDSA with SHA-256)
      { alg: -257, type: 'public-key' }, // RS256 (RSA with SHA-256)
    ],
    authenticatorSelection,
    timeout: 60000, // 60 second timeout
    attestation: 'none', // Don't need attestation for our use case
    hints,
  };

  try {
    // 4. Wait for page focus so the platform authenticator UI can be invoked
    // (browsers refuse the call with NotAllowedError when the page is unfocused).
    await waitForFocus();

    // 5. Create credential
    const credential = await navigator.credentials.create({
      publicKey: publicKeyCredentialCreationOptions,
    }) as PublicKeyCredential | null;

    if (!credential) {
      throw new PasskeyError(
        'CREATION_FAILED',
        'Failed to create passkey credential. Please try again.'
      );
    }

    // 5. Extract credential data
    const rawId = new Uint8Array(credential.rawId);
    const response = credential.response as AuthenticatorAttestationResponse;

    // getPublicKey() may not be available in all browsers, handle gracefully
    let publicKeyBytes: Uint8Array | null = null;
    try {
      const publicKey = response.getPublicKey();
      if (publicKey) {
        publicKeyBytes = new Uint8Array(publicKey);
      }
    } catch (e) {
      logger.warn('Could not extract public key:', e);
    }

    // Capture the transports the authenticator advertises so unlock can
    // replay them in `allowCredentials.transports`. New registrations are
    // limited to platform (`internal`) and roaming security keys (`usb`,
    // `nfc`, `ble`); legacy credentials may also carry `hybrid`/`cable`
    // from when QR pairing was offered, and those transports must still
    // round-trip so existing users aren't locked out when online.
    // getTransports() may be absent on older browsers; treat that as
    // "we don't know" and store an empty list.
    let transports: string[] = [];
    try {
      const getTransports = (response as AuthenticatorAttestationResponse & {
        getTransports?: () => string[];
      }).getTransports;
      if (typeof getTransports === 'function') {
        transports = getTransports.call(response) ?? [];
      }
    } catch (e) {
      logger.warn('Could not extract transports:', e);
    }

    // Defense in depth: `hints` is advisory and the OS picker may still
    // route the user through cross-device QR pairing despite our request.
    // Only refuse the credential if it is *exclusively* hybrid/cable —
    // i.e. no offline-capable transport is also present. iCloud-synced
    // passkeys and password-manager-backed credentials commonly advertise
    // `internal` plus `hybrid` (the latter as a "this passkey is also
    // usable cross-device" hint); those are fine on the registering
    // device because `internal` covers the offline path.
    const hasHybrid = transports.some(t => t === 'hybrid' || t === 'cable');
    const hasOfflineCapable = transports.some(t => OFFLINE_CAPABLE_TRANSPORTS.has(t));
    if (hasHybrid && !hasOfflineCapable) {
      // The credential already exists on the authenticator (iCloud
      // Keychain, 1Password vault, paired phone, etc) — WebAuthn doesn't
      // give the RP a way to delete it, so we can only warn the user it
      // is stranded and let them clean it up if they care.
      throw new PasskeyError(
        'OFFLINE_INCAPABLE_TRANSPORT',
        'This passkey was registered for cross-device use, which requires the internet, so the app could not save it. The credential still exists on your authenticator (you may want to delete it from there). Please pick "Use this device or a password manager" or "Use a security key" so the app can unlock offline.'
      );
    }

    // 6. Generate salt for key derivation (16 bytes)
    const salt = crypto.getRandomValues(new Uint8Array(16));

    // 7. Create credential object
    const passkeyCredential: BiometricCredential = {
      credentialId: bufferToHex(rawId),
      publicKey: publicKeyBytes ? bufferToBase64(publicKeyBytes) : '',
      salt: bufferToHex(salt),
      userId,
      scope: 'user',
      transports,
      createdAt: new Date().toISOString(),
    };

    // 8. Store credential in Dexie biometricCredentials table (NOT localStorage)
    await savePasskeyCredential(passkeyCredential);

    // Emit an explicit success counterpart to `passkey.register.attempt` so
    // the registration funnel (attempt → success or exception) is directly
    // queryable in Sentry rather than being inferred from absence. Uses the
    // Logs API rather than captureMessage so it doesn't create an issue.
    Sentry.logger.info('passkey.register.success', {
      passkey_step: 'register',
      platform_authenticator_available: platformAvailable,
      method: method ?? 'auto',
    });

    logger.debug('Passkey credential registered successfully');
    return passkeyCredential;
  } catch (error) {
    // Skip Sentry capture for NOT_FOCUSED — it's a user-environment issue
    // (background tab when WebAuthn was invoked), not a code defect, and
    // the user simply needs to refocus and retry.
    if (error instanceof PasskeyError && error.code === 'NOT_FOCUSED') {
      throw error;
    }

    // OFFLINE_INCAPABLE_TRANSPORT also isn't a defect — it fires when the
    // OS picker overrode our advisory `hints` and routed the user through
    // cross-device QR pairing. Log it as a warning (queryable, but doesn't
    // create an issue group) so we can monitor whether our hints are
    // being honored across browser/OS combinations without the noise of
    // a real exception group.
    if (error instanceof PasskeyError && error.code === 'OFFLINE_INCAPABLE_TRANSPORT') {
      Sentry.logger.warn('passkey.register.offline_incapable_transport', {
        passkey_step: 'register',
        method: method ?? 'auto',
        platform_authenticator_available: platformAvailable,
      });
      throw error;
    }

    // DOMException doesn't always inherit from Error in every runtime, so
    // duck-type on `.name`.
    const errorName = typeof (error as { name?: unknown })?.name === 'string'
      ? (error as { name: string }).name
      : 'unknown';

    // NotAllowedError is the browser's single answer for "the user dismissed
    // the dialog", "the 60s timeout elapsed" and "the authenticator refused"
    // — a user-driven outcome, not a defect (and by far the most common
    // registration failure: users whose org disables Windows Hello are only
    // offered a security key they may not have). Log it like the transport
    // case above so the funnel stays queryable without an issue group.
    if (errorName === 'NotAllowedError') {
      Sentry.logger.warn('passkey.register.not_allowed', {
        passkey_step: 'register',
        method: method ?? 'auto',
        platform_authenticator_available: platformAvailable,
      });
      throw handleWebAuthnError(error);
    }

    // Surface the remaining registration failures to Sentry with enough
    // context to tell capability problems ("device can't be used") apart.
    Sentry.captureException(error, {
      tags: {
        passkey_step: 'register',
        platform_authenticator_available: platformAvailable,
        method: method ?? 'auto',
        error_name: errorName,
      },
    });
    throw handleWebAuthnError(error);
  }
}

// Map a stored transports list to the WebAuthn `AuthenticatorTransport` enum.
// `cable` is the pre-L3 spelling of `hybrid` and is no longer in the WebIDL
// enum; strict browsers may throw if we pass it through, so we rewrite it.
// Anything else outside the enum is dropped (the browser will fall back to
// "try every transport" for that credential id, which is the safer choice).
const KNOWN_TRANSPORTS = new Set(['internal', 'usb', 'nfc', 'ble', 'hybrid']);
// Transports that work without an internet relay. Used at registration
// time to gate the offline-only invariant: a credential that advertises
// any of these alongside `hybrid` is still fine; one whose only
// transports are `hybrid`/`cable` is rejected.
const OFFLINE_CAPABLE_TRANSPORTS = new Set(['internal', 'usb', 'nfc', 'ble']);
function sanitizeStoredTransports(stored: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of stored) {
    const t = raw === 'cable' ? 'hybrid' : raw;
    if (KNOWN_TRANSPORTS.has(t) && !seen.has(t)) {
      seen.add(t);
      result.push(t);
    }
  }
  return result;
}

/**
 * Authenticate using passkey and derive encryption key
 * This should be called when unlocking the screen
 *
 * @returns Promise resolving to the derived encryption key (32 bytes) and
 *   the raw WebAuthn credential id from the assertion. The rawId is exposed
 *   so callers can re-derive a key under different parameters (e.g. silent
 *   migration of a legacy person-scoped credential).
 * @throws PasskeyError if authentication fails
 */
export async function authenticatePasskey(): Promise<{
  key: Uint8Array;
  rawId: Uint8Array;
  authenticatorAttachment: AuthenticatorAttachment | null;
}> {
  // 0. Ensure database is open before reading credentials
  // This is needed because the database may be closed after locking
  await ensureDatabaseOpen();

  // 1. Retrieve stored credential from biometricCredentials table
  const storedCredential = await getPasskeyCredential();

  if (!storedCredential) {
    throw new PasskeyError(
      'NOT_REGISTERED',
      'No passkey credential found. Please register passkey authentication first.'
    );
  }

  // 2. Check WebAuthn support
  if (!isWebAuthnSupported()) {
    throw new PasskeyError(
      'NOT_SUPPORTED',
      'WebAuthn is not supported in this browser'
    );
  }

  // 3. Generate new challenge
  const challenge = crypto.getRandomValues(new Uint8Array(32));

  // 4. Create assertion options.
  // Replay the transports captured at registration. New credentials only
  // carry offline-capable transports (`internal`, `usb`, `nfc`, `ble`),
  // but credentials registered before the offline-only change may carry
  // `hybrid` or its older alias `cable`; we still pass those through so
  // the user can authenticate when online and re-register an offline-
  // capable method at their convenience. Sanitize the list before
  // passing it to WebAuthn: the older `cable` string is not in the
  // current `AuthenticatorTransport` WebIDL enum, and strict
  // implementations may throw on unknown values, so map it to `hybrid`
  // and drop anything else we don't recognize. For credentials saved
  // before transports were stored at all, omit the field so the browser
  // tries whatever transport works for that credential id — safer than
  // hardcoding `internal`.
  const storedTransports = sanitizeStoredTransports(storedCredential.transports ?? []);
  const allowCredential: PublicKeyCredentialDescriptor = {
    id: hexToBuffer(storedCredential.credentialId) as BufferSource,
    type: 'public-key',
    ...(storedTransports.length > 0
      ? { transports: storedTransports as AuthenticatorTransport[] }
      : {}),
  };
  const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
    challenge,
    allowCredentials: [allowCredential],
    userVerification: 'required',
    timeout: 60000,
  };

  try {
    // 5. Wait for page focus so the platform authenticator UI can be invoked
    // (browsers refuse the call with NotAllowedError when the page is unfocused).
    await waitForFocus();

    // 6. Get assertion (authenticate)
    const assertion = await navigator.credentials.get({
      publicKey: publicKeyCredentialRequestOptions,
    }) as PublicKeyCredential | null;

    if (!assertion) {
      throw new PasskeyError(
        'AUTHENTICATION_FAILED',
        'Passkey authentication failed. Please try again.'
      );
    }

    // 6. Derive encryption key from rawId + salt + stored userId.
    // The credential's `scope` is metadata only — key derivation always
    // uses whatever userId was stored at registration time, so legacy
    // person-scoped credentials and new user-scoped credentials are
    // unlocked by the exact same code path.
    const rawId = new Uint8Array(assertion.rawId);
    const salt = hexToBuffer(storedCredential.salt);
    const encryptionKey = await deriveEncryptionKey(rawId, salt, storedCredential.userId);

    logger.debug('Passkey authentication successful');
    // authenticatorAttachment is "platform" (built-in biometric like Touch ID,
    // Windows Hello) or "cross-platform" (security key like YubiKey). Older
    // browsers may return null when they can't determine it.
    const authenticatorAttachment = (assertion.authenticatorAttachment ?? null) as AuthenticatorAttachment | null;
    return { key: encryptionKey, rawId, authenticatorAttachment };
  } catch (error) {
    throw handleWebAuthnError(error);
  }
}

/**
 * Check if passkey credential is registered
 */
export async function isPasskeyRegistered(): Promise<boolean> {
  return await isPasskeyRegisteredService();
}

/**
 * Clear passkey credential (e.g., during logout)
 * This does NOT revoke the credential from the authenticator
 */
export async function clearPasskeyCredential(): Promise<void> {
  await clearPasskeyCredentialService();
  logger.debug('Passkey credential cleared');
}

/**
 * Handle WebAuthn errors and convert to PasskeyError
 */
function handleWebAuthnError(error: unknown): PasskeyError {
  if (error instanceof PasskeyError) {
    return error;
  }

  if (error instanceof DOMException) {
    switch (error.name) {
      case 'NotAllowedError':
        return new PasskeyError(
          'NOT_ALLOWED',
          'Passkey authentication was cancelled or not allowed by the user.'
        );
      case 'NotSupportedError':
        return new PasskeyError(
          'NOT_SUPPORTED',
          'Passkey authentication is not supported on this device.'
        );
      case 'SecurityError':
        return new PasskeyError(
          'SECURITY_ERROR',
          'Security error during passkey authentication. This may be due to an insecure context (not HTTPS).'
        );
      case 'AbortError':
        return new PasskeyError(
          'ABORTED',
          'Passkey authentication was aborted.'
        );
      case 'InvalidStateError':
        return new PasskeyError(
          'INVALID_STATE',
          'This device is already registered. Please clear existing registration first.'
        );
      case 'NotFoundError':
        return new PasskeyError(
          'NOT_FOUND',
          'No matching passkey credential found. Please register again.'
        );
      case 'TimeoutError':
        return new PasskeyError(
          'TIMEOUT',
          'Passkey authentication timed out. Please try again.'
        );
      default:
        return new PasskeyError(
          'UNKNOWN',
          `Passkey authentication failed: ${error.message}`
        );
    }
  }

  if (error instanceof Error) {
    return new PasskeyError(
      'UNKNOWN',
      `Passkey authentication failed: ${error.message}`
    );
  }

  return new PasskeyError(
    'UNKNOWN',
    'An unknown error occurred during passkey authentication'
  );
}
